import { useState } from 'react';
import { LedgerEntry, EntryType } from '@/types/billing.types';
import { useGetBillingHistoryQuery } from '../../../api/billingApi';
import { ArrowUpCircle, ArrowDownCircle, RotateCcw, Loader2 } from 'lucide-react';

const ENTRY_TYPE_LABELS: Record<EntryType, string> = {
  grant: 'Plan Grant',
  topup: 'Top-Up',
  usage: 'Usage',
  refund: 'Refund',
  expiry: 'Expired',
};

const ENTRY_TYPE_COLORS: Record<EntryType, string> = {
  grant: '#10B981',
  topup: 'var(--color-status-sky, #38bdf8)',
  usage: '#f87171',
  refund: '#10B981',
  expiry: '#94a3b8',
};

function EntryIcon({ type }: { type: EntryType }) {
  const color = ENTRY_TYPE_COLORS[type];
  const size = 16;
  if (type === 'usage' || type === 'expiry')
    return <ArrowDownCircle size={size} color={color} />;
  if (type === 'refund')
    return <RotateCcw size={size} color={color} />;
  return <ArrowUpCircle size={size} color={color} />;
}

import { BillingHistorySkeleton } from '@/features/admin/components/loaders/BillingSkeletons';

export function BillingHistory() {
  const [page, setPage] = useState(1);
  const limit = 15;

  const { data, isFetching } = useGetBillingHistoryQuery({ page, limit });

  const entries: LedgerEntry[] = data?.data?.data ?? [];
  const totalPages = data?.data?.pagination?.totalPages ?? 1;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {isFetching ? (
        <BillingHistorySkeleton />
      ) : entries.length === 0 ? (
        <div
          style={{
            padding: '48px',
            textAlign: 'center',
            color: 'var(--admin-card-subtext)',
            fontSize: '14px',
          }}
        >
          No credit transactions yet.
        </div>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Type', 'Reason', 'Credits', 'Balance After', 'Date'].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: 'var(--admin-card-subtext)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid var(--admin-card-border)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  style={{ borderBottom: '1px solid var(--admin-card-border)' }}
                >
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <EntryIcon type={entry.entry_type} />
                      <span
                        style={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--admin-card-text)',
                        }}
                      >
                        {ENTRY_TYPE_LABELS[entry.entry_type] ?? entry.entry_type}
                      </span>
                    </div>
                  </td>
                  <td
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      padding: '12px',
                      fontSize: '13px',
                      color: 'var(--admin-card-subtext)',
                      textTransform: 'capitalize',
                    }}
                  >
                    {entry.reason?.replace(/_/g, ' ') ?? '-'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 700,
                        fontSize: '13px',
                        color: ENTRY_TYPE_COLORS[entry.entry_type],
                      }}
                    >
                      {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                    </span>
                  </td>
                  <td
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      padding: '12px',
                      fontSize: '13px',
                      color: 'var(--admin-card-text)',
                      fontWeight: 600,
                    }}
                  >
                    {entry.balance_after.toLocaleString()}
                  </td>
                  <td
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      padding: '12px',
                      fontSize: '12px',
                      color: 'var(--admin-card-subtext)',
                    }}
                  >
                    {formatDate(entry.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '16px',
              }}
            >
              <div style={{ fontSize: '13px', color: 'var(--admin-card-subtext)' }}>
                Showing page {page} of {totalPages}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '0',
                    border: '1px solid var(--admin-card-border)',
                    background: 'transparent',
                    color: page === 1 ? 'var(--admin-card-border)' : 'var(--admin-card-text)',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  Previous
                </button>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '0',
                    border: '1px solid var(--admin-card-border)',
                    background: 'transparent',
                    color: page === totalPages ? 'var(--admin-card-border)' : 'var(--admin-card-text)',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
